import React, { useCallback, useState } from "react";
import { Modal, Button, Typography, Table, Empty, Space } from "antd";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CloseOutlined } from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useDropzone } from "react-dropzone";
import DropzoneWrapper from "../../../components/util/Dropzone.style";
import { ReactComponent as CloudImg } from "../../../assets/img-cloud.svg";
import { uploadMeser } from "../prospectusesSlice";
import { useLocation } from "react-router-dom";
import { exportReportToPdf } from "../../../components/util/PDFExporter";
import moment from "moment";
const { Title, Text } = Typography;

const UploadMeserFile = (props) => {
  const { hide, visible, branch_id } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  let location = useLocation();

  const loading = useSelector(
    (state) => state.pendingActions["prospectuses/uploadMeserFile"]
  );
  const onDrop = useCallback((acceptedFiles) => {
    // Take only the last dropped file
    const file = acceptedFiles[acceptedFiles.length - 1];
    if (file) {
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
        fileName: file.name,
      });
      setFiles([newFile]); // Replace existing files with new one
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 10485760,
    multiple: false,
  });
  const onDecline = () => {
    hide();
    setFiles([]);
    setResults(null); // Reset results when closing
  };

  const onCancel = () => {
    setFiles([]);
    setResults(null); // Reset results when canceling
    hide();
  };

  const onCloseResults = () => {
    setResults(null); // Reset results when closing results view
    setFiles([]);
    hide();
  };

  const setHeader = (doc, record) => {
    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.setR2L(true);
    doc.setFont("Rubik", "normal"); // set font
    var title = "תוצאות עיבוד קובץ מסר";
    const margins = 10;
    var hLoc = doc.internal.pageSize.getWidth() - margins;
    const report = "תאריך הדפסה";
    const date = `${moment().format("HH:mm DD/MM/YYYY")}`;
    const reportDate = `${report} - ${date}`;
    doc.text(title, hLoc, 20, {
      align: "right",
    });
    doc.setFontSize(12);

    doc.text(reportDate, margins, 20, {
      align: "left",
    });
  };

  const handleSubmit = async () => {
    const margins = 10;
    const items = results.map((item) => {
      return {
        patient_name: item.patient_name,
        patient_id: item.patient_id,
        line_code: item.line_code,
        status: item.success
          ? intl.formatMessage({ id: "app.prospectuses.meser.success" })
          : intl.formatMessage({ id: "app.prospectuses.meser.error" }),
        error: item.error,
      };
    });

    exportReportToPdf(
      intl.formatMessage({ id: "app.patients.houseVisitsReport" }),
      items,
      {
        setHeader,
        headerRecord: null,
        intl,
        category: "analytics",
        tableOptions: {
          styles: { fontSize: 9 },
          margin: { top: 26, left: margins, right: margins },
        },
        orientation: "l",
        action: "print",
        reverseText: true,
      }
    );
  };

  const columns = [
    {
      title: intl.formatMessage({ id: "app.prospectuses.meser.patient_name" }),
      dataIndex: "patient_name",
      key: "patient_name",

      render: (patient_name, record) => {
        const isAdmin = location.pathname.includes("admin");
        const linkPath = isAdmin
          ? `/admin/patients/${record.patient_id}`
          : `/branches/${record.branch}/patients/${record.patient_id}`;

        return (
          <Link
            style={{ color: "inherit", flexGrow: 1, minWidth: 0 }}
            to={{
              pathname: linkPath,
            }}
          >
            <Text
              ellipsis={{ tooltip: patient_name }}
              style={{
                padding: "0 10px",
                textDecoration: "underline",
                width: "100%",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {patient_name}
            </Text>
          </Link>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.meser.patient_id" }),
      dataIndex: "patient_id",
      key: "patient_id",
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.meser.line_code" }),
      dataIndex: "line_code",
      key: "line_code",
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.meser.status" }),
      dataIndex: "success",
      key: "success",
      render: (success) => (
        <Text type={success ? "success" : "danger"}>
          <FormattedMessage
            id={
              success
                ? "app.prospectuses.meser.success"
                : "app.prospectuses.meser.error"
            }
          />
        </Text>
      ),
    },
    {
      title: intl.formatMessage({ id: "app.prospectuses.meser.error" }),
      dataIndex: "error",
      key: "error",
      render: (error) =>
        error ? (
          <FormattedMessage id={`app.prospectuses.meser.errors.${error}`} />
        ) : null,
    },
  ];

  const onUpload = async () => {
    for (let i = 0; i < files.length; i++) {
      const params = {
        file: files[i],
        branch_id,
      };
      const action = await dispatch(uploadMeser(params));
      const items = action?.payload || [];
      if (!action.error) {
        setResults(items);
      }
    }
  };

  const renderContent = () => {
    if (results !== null) {
      if (results.length === 0) {
        return (
          <Empty
            description={
              <FormattedMessage id="app.messages.no_results_found" />
            }
          />
        );
      }
      return (
        <div style={{ padding: "20px 0" }}>
          <Table
            dataSource={results}
            columns={columns}
            rowKey="patient_id"
            pagination={true}
            scroll={{ y: 400 }}
            size="small"
            bordered
          />
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Space>
              <Button onClick={onCloseResults} type="primary">
                <FormattedMessage id="app.actions.close" />
              </Button>
              <Button onClick={handleSubmit}>
                <FormattedMessage id="app.actions.print" />
              </Button>
            </Space>
          </div>
        </div>
      );
    }
    return (
      <DropzoneWrapper>
        <div
          {...getRootProps({ className: "outer" })}
          style={{ padding: "0 60px" }}
        >
          <CloudImg />
          <input {...getInputProps()} />
          <p className="title">
            {<FormattedMessage id="app.actions.dragAndDropMeser" />}
          </p>
          <p className="subtitle">
            {<FormattedMessage id="app.actions.dragAndDropBtl2" />}
          </p>
          <p className="limit">
            {
              <FormattedMessage
                id={
                  process.env.REACT_APP_CONFIG === "matav"
                    ? "app.actions.dragAndDropMatav3"
                    : "app.actions.dragAndDrop3"
                }
              />
            }
          </p>
        </div>
        <div className="filesContainer">
          {files.length > 0 ? (
            <Title level={3} style={{}}>
              {<FormattedMessage id="app.titles.files" />}
            </Title>
          ) : null}
          {files.map((f) => (
            <Text key={f.path}>{f.name}</Text>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "10px",
          }}
        >
          <div>
            <Button
              disabled={files.length < 1 || loading}
              type="primary"
              htmlType="submit"
              loading={loading}
              onClick={onUpload}
              size="large"
            >
              {<FormattedMessage id="app.actions.uploadFile" />}
            </Button>
            <Button
              style={{ margin: "0 10px" }}
              onClick={onCancel}
              size="large"
            >
              {<FormattedMessage id="app.actions.cancel" />}
            </Button>
          </div>
        </div>
      </DropzoneWrapper>
    );
  };

  return (
    <Modal
      closable={false}
      maskClosable={false}
      open={visible}
      footer={null}
      onCancel={hide}
      title={null}
      width={"fit-content"}
      styles={{
        body: {
          padding: "20px 100px",
        },
      }}
      destroyOnClose={true}
    >
      <div>
        <div className="header" style={{ height: "40px" }}>
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onDecline}
          />
        </div>
        <Title
          style={{
            fontSize: "30px",
            fontWeight: 500,
            lineHeight: 1.27,
            color: "rgba(0, 0, 0, 0.85)",
            margin: "13px 0 40px 0 ",
            textAlign: "center",
          }}
        >
          <FormattedMessage
            id={
              results !== null
                ? "app.prospectuses.meserTableTitle"
                : "app.actions.uploadTitle"
            }
          />
        </Title>

        {renderContent()}
      </div>
    </Modal>
  );
};

export default UploadMeserFile;
